import io from "socket.io-client";
import {
  WEBSOCKET_OPENED,
  WEBSOCKET_CLOSED,
  ESTABLISH_STATES_DEVICE,
  WEBSOCKET_REAUTHENTICATING,
} from "../../actions/types";
import _ from "lodash";
import { displayPermanentErrorNotification } from "../../actions";
import { getActiveOrg } from "../../utils/activeOrg";

class SocketIOClient {
  constructor(store, socketUrl, getToken) {
    this.store = store;
    this.socketUrl = socketUrl;
    this.getToken = getToken;
    this.socketId = "";
    this.failCount = 0;

    this.checkStop = (socket, count) => {
      if (count > 10) {
        this.store.dispatch(
          displayPermanentErrorNotification(
            "Lost Connection. Please refresh the page and try again.",
          ),
        );
        socket.disconnect();
      }
      socket.failCount = 0;
    };

    this.debounceCheck = _.debounce(this.checkStop, 30000, { maxWait: 30000 });

    this.socket =
      process.env.REACT_APP_TEST_STATUS === "test"
        ? io(this.socketUrl)
        : io(this.socketUrl, {
            auth: (cb) => this.getAuth().then((resp) => cb(resp)),
            timeout: 2000,
            reconnectionDelay: 500,
            reconnectionDelayMax: 2000,
            path: "/ws/socket.io",
            withCredentials: true,
            // transports: [ "websocket" ],
          });

    // Proactively recover from token expiration or loss of connection
    this.recovering = false;
    this.recoverConnection = async (forceRefresh = false) => {
      if (this.recovering || this.socket?.connected) return;
      this.recovering = true;
      try {
        // Pass forceRefresh=true to bypass cache and re-mint, so a server kick doesn't loop on the bad token
        await this.getToken(forceRefresh);
        if (this.socket && !this.socket.connected) {
          this.socket.connect();
        }
      } catch (e) {
        console.warn("Websocket recovery failed", e);
      } finally {
        this.recovering = false;
      }
    };

    this.handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        this.recoverConnection();
      }
    };

    // Wrap so the DOM Event isn't passed as `forceRefresh`
    this.handleWake = () => this.recoverConnection();

    if (process.env.REACT_APP_TEST_STATUS !== "test") {
      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange,
      );
      window.addEventListener("focus", this.handleWake);
      window.addEventListener("online", this.handleWake);
    }

    this.socket.on("connect_error", (err) => {
      this.failCount += 1;

      if (err.message === "invalid credentials") {
        this.debounceCheck(this.socket, this.failCount);
      }
    });

    this.socket.on("connect", () => {
      this.failCount = 0;
      this.store.dispatch({ type: WEBSOCKET_OPENED });
    });

    this.socket.io.on("reconnect_attempt", (attempt) => {
      console.warn("Websocket reconnect attempt", attempt);
    });

    this.socket.on("disconnect", (reason) => {
      this.store.dispatch({ type: WEBSOCKET_CLOSED });
      // On "io server disconnect" the server explicitly dropped us and Socket.IO will NOT auto-reconnect, so reconnect manually
      if (reason === "io server disconnect" && this.socket) {
        // Server explicitly kicked us — don't trust the cached token; force a re-mint.
        this.recoverConnection(true);
      }
    });

    this.socket.io.on("reconnect", () => {
      this.store.dispatch({ type: WEBSOCKET_REAUTHENTICATING });
    });

    this.socket.on("message", (action) => {
      if (action.type === ESTABLISH_STATES_DEVICE) {
        this.socketId = action.payload.socketId;
      }
      this.store.dispatch(action);
    });
  }

  // A socket has no request headers to carry the active organization on, so it
  // travels in the handshake auth payload instead. The server compares it
  // against the cached session for socketId: if they disagree it starts a fresh
  // session rather than resurrecting the old organization's one, so a reconnect
  // after a switch cannot silently put the user back where they were.
  async getAuth() {
    const token = await this.getToken();
    const organization = getActiveOrg();
    const auth = { token };
    if (organization) auth.organization = organization;
    if (this.socketId.length > 0) auth.socketId = this.socketId;
    return auth;
  }

  send(action) {
    this.socket.emit("message", { ...action, t: +new Date() });
  }

  // Call destroy() to cleanup whenever the client is discarded (logout, org switch, re-instantiation)
  // currently there is no logout behavior that doesn't result in a page refresh, but this contains the required logic for future use.
  destroy() {
    if (process.env.REACT_APP_TEST_STATUS !== "test") {
      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange,
      );
      window.removeEventListener("focus", this.handleWake);
      window.removeEventListener("online", this.handleWake);
    }
    if (this.socket?.io) {
      this.socket.io.off("reconnect_attempt");
      this.socket.io.off("reconnect");
    }
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }
  }
}

export default SocketIOClient;
